<script setup>
import {addModal,removeModal} from '~/stores/modal';
import {navigationItems,navigationIsBranch} from '~/lib/navigation';
import {mobileFeedItem,mobileContentSearchItem} from '~/utils/mobile-post-navigation.mjs';
import CommonModals from "~/components/Common/Modals.vue";
import "~/assets/css/site-shell.css";
import "~/assets/css/shared-controls.css";
import "~/assets/css/mobile-navigation.css";
// FIXME нужно иметь возможность сделать несколько разных инстансов нотификаций / модалок
import { notifications } from "@/stores/notification";

const props = defineProps(["links"]);
const route = useRoute();
useHead(() => ({
    link: [
        {
            key: "canonical",
            rel: "canonical",
            href: "https://uogorod.ru" + route.path,
        },
    ],
}));
const client = useUser();
const navigationReady = ref(false);
onMounted(() => { navigationReady.value = true; });
const isAdminArea = computed(() => route.path === '/admin' || route.path.startsWith('/admin/'));
const catalogSearchOpen=ref(false),postSearchOpen=ref(false);
const authorizedLinks = computed(() => navigationItems(props.links || [], client.value, route.path));
const isMenuOpen = useState("drawer", () => false);
const toggleMenu = () => (isMenuOpen.value = !isMenuOpen.value);
watch(() => route.fullPath, () => {
    if(catalogSearchOpen.value||postSearchOpen.value)removeModal();
    if (!navigationIsBranch(authorizedLinks.value, route.path)) isMenuOpen.value = false;
});
const closeMenuOnEscape = (event) => {
    if (event.key === 'Escape' && isMenuOpen.value) {
        isMenuOpen.value = false;
    }
};
onMounted(() => window.addEventListener('keydown', closeMenuOnEscape));
onBeforeUnmount(() => window.removeEventListener('keydown', closeMenuOnEscape));

const logout = async () => {
    await useFetch("/api/auth/logout");
    navigateTo("/");
};

const emitter = useEmitter();
const messenger=useMessenger();
const unreadedCount=computed(()=>messenger.state.value.unread);
function mobileCatalogSearch(){if(catalogSearchOpen.value)return;catalogSearchOpen.value=true;addModal({title:'Поиск по каталогу',class:'uo-catalog-search-modal',component:defineAsyncComponent(()=>import('~/components/Modals/CatalogSearch.vue')),closeCallback(){catalogSearchOpen.value=false;nextTick(()=>document.querySelector('.uo-mobile-general button[aria-label="Поиск по каталогу"]')?.focus());}});}
function mobilePostSearch(){if(postSearchOpen.value)return;postSearchOpen.value=true;addModal({title:'Поиск по постам',class:'uo-post-search-modal',component:defineAsyncComponent(()=>import('~/components/Modals/PostSearch.vue')),closeCallback(){postSearchOpen.value=false;nextTick(()=>document.querySelector('.uo-mobile-general button[aria-label="Поиск по постам"]')?.focus());}});}
function mobileAction(action){if(action==='catalog-search')mobileCatalogSearch();else if(action==='post-search')mobilePostSearch();else if(action==='more')mobileMore();}
function mobileMore(){const primary=new Set(mobileNavigationItems.value.map(item=>item.to).filter(Boolean));const items=authorizedLinks.value.filter(item=>item.access!==false).flatMap(item=>[item,...(item.childrens||[]).filter(child=>child.access!==false)]).filter(item=>!primary.has(item.path)).map(item=>({label:item.title,icon:item.icon||'arrow',run:()=>navigateTo(item.path)}));addModal({title:'Все разделы',class:'uo-mobile-options-modal',component:defineAsyncComponent(()=>import('~/components/Common/MobileMenuOptions.vue')),componentProps:{items},events:{choose(index){const item=items[index];if(!item)return;removeModal();item.run();}}});}
const mobileNavigationItems=computed(()=>[
 {label:'Журнал',icon:'book',to:client.value?.client_id?'/profiles/'+client.value.client_id+'/gardens':'/auth/signin',active:/^\/profiles\/\d+\/gardens(?:\/|$)/.test(route.path)},
 mobileFeedItem(route.path),
 {label:'Чаты',ariaLabel:'Сообщения',icon:'message',to:'/messages',badge:unreadedCount.value,active:route.path==='/messages'||route.path.startsWith('/messages/')},
 mobileContentSearchItem(route.path,catalogSearchOpen.value,postSearchOpen.value),
 {label:'Ещё',icon:'more',action:'more'},
]);

useSeoMeta({
    ogSiteName: "Умный огород",
});
</script>

<style>
body:has(.drawer--opened) [form="toggle-menu"] svg:not(.hidden) {
    display: none;
}

body:has(.drawer--opened) [form="toggle-menu"] svg.hidden {
    display: block;
}

</style>

<template>
    <form id="toggle-menu" @submit.prevent="() => toggleMenu()"></form>
    <CommonHeader>
        <template v-if="client?.client_id" #end>
            <NotificationsInbox />
            <button class="uo-shell-icon uo-shell-message-trigger relative" @click="() => emitter.emit('chat:open')"
                type="button" :aria-label="unreadedCount ? 'Сообщения, непрочитанных диалогов: '+unreadedCount : 'Сообщения'" :title="unreadedCount ? 'Непрочитанных диалогов: '+unreadedCount : 'Сообщения'" tabindex="0">
                <span class="uo-notification-anchor"><CommonMobileMenuIcon name="message"/><CommonNotificationCount :count="unreadedCount"/></span>
            </button>
            <div class="flex items-center text-black/h">
                <form class="group relative grid place-content-center cursor-pointer" :key="client">
                    <button class="uo-shell-icon uo-profile-trigger" aria-label="Профиль" type="button"
                        tabindex="0">
                        <Userpic class="w-auto block pointer-events-none"
                            :src="`/files/userpic/${client?.client_id}.webp`" width="32" height="32"
                            :name="client?.name" />
                    </button>
                    <div
                        class="dropdown absolute top-10 right-0 bg-white rounded-xl shadow-lg border-[0.5px] p-4 grid gap-1 whitespace-nowrap place-items-start">
                        <nuxt-link tabindex="0" class="relative flex items-center gap-2" :to="{
                            path: `/profiles/${client?.client_id}/gardens`,
                        }" prefetch-on="interaction">
                            <svg-icon name="account-circle-outline" width="16" height="16" />
                            Журнал
                        </nuxt-link>
                        <nuxt-link tabindex="0" class="relative flex items-center gap-2"
                            :to="{ path: `/profiles/${client?.client_id}` }" prefetch-on="interaction">
                            <svg-icon name="account-circle-outline" width="16" height="16" />
                            Мой профиль
                        </nuxt-link>
                        <nuxt-link tabindex="0" class="flex items-center gap-2" :to="{ path: `/feed/new/edit` }">
                            <svg-icon name="note-plus-outline" width="16" height="16" />
                            Добавить пост
                        </nuxt-link>
                        <nuxt-link tabindex="0" class="flex items-center gap-2" :to="{
                            path: `/profiles/${client?.client_id}/edit`,
                        }">
                            <svg-icon name="gear-outline" width="16" height="16" />
                            Настройки
                        </nuxt-link>
                        <button class="flex items-center gap-2 font-normal" @click="logout" type="button" tabindex="0">
                            <svg-icon name="power-standby" width="16" height="16" />
                            Выход
                        </button>
                    </div>
                </form>
            </div>
        </template>
    </CommonHeader>

    <div class="uo-shell-layout w-full mx-auto flex flex-1">
        <CommonNavigation :key="navigationReady ? 'ready' : 'server'" :items="authorizedLinks"
            :path="route.path" :open="isMenuOpen" :admin="isAdminArea" @close="isMenuOpen = false" />
        <main class="uo-shell-main flex flex-col gap-2 w-full max-w-screen-lg mx-auto">
            <CommonHelpBanner />
            <slot />
        </main>
    </div>
    <ClientOnly>
        <ChatHost />
        <CommonModals />
        <div class="fixed top-24 right-4 grid gap-4 z-30">
            <!-- Стандартные уведомления (error|warn|info) -->
            <Notification :notification="notification" v-for="notification in notifications" />
        </div>
        <CommonCommunityContact />
        <div v-if="!client?.client_id" class="uo-floating-help fixed grid left-0 bottom-0 z-20 gap-4 p-4"><BannersJournal class="w-full" /></div>
    </ClientOnly>
    <CommonMobileActionBar v-if="!isAdminArea" :items="mobileNavigationItems" @action="mobileAction"/>
    <CommonFooter />
</template>
